import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import { Button, Loader, Modal } from 'semantic-ui-react';
import { commitMutation, graphql } from 'react-relay';
import { GlobalNotifications, Intent, MobileModal } from '../../Modules';
import { FloatingLabelInput as Input, Success } from '../../Elements';
import styleVariables from '../../variables';
import Formsy from 'formsy-react';
import SignatureBlockShow from '../Sections/SignatureBlocks/SignatureBlockShow';
import styles from './Contract.scss';
import nullifyEmptyStrings from '../../../lib/utils/nullifyEmptyStrings';
import SignaturePad from './SignaturePad';
import filestack from 'filestack-js';
import '../../../lib/utils/polyfills/toBlob';
import signatureBlockSizeClass from '../Sections/SignatureBlocks/SignatureBlockSizeClass';

const apikey = process.env.FILESTACK_API_KEY;
const client = filestack.init(apikey);

export default class SignContractFlow extends React.Component {
  static propTypes = {
    contract: PropTypes.object.isRequired,
    onOpen: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    open: PropTypes.bool.isRequired,
    relay: PropTypes.object.isRequired,
    signatureBlock: PropTypes.object.isRequired,
    trigger: PropTypes.node.isRequired
  };

  constructor(props) {
    super(props);
    this.state = {
      localSignatureBlock: _.cloneDeep(this.props.signatureBlock),
      window: { width: 0, height: 0 },
      step: this.signatureBlockFields(this.props.signatureBlock)
        ? 'signatureBlock'
        : 'sign',
      submitting: false,
      submitted: false,
      signatureSubmitted: false,
      signatureFormat: 'type'
    };
    this.onSubmit = this.onSubmit.bind(this);
    this.signatureBlockFields = this.signatureBlockFields.bind(this);
    this.onValidSignatureBlockFormSubmit = this.onValidSignatureBlockFormSubmit.bind(
      this
    );
    this.onInvalidSignatureBlockFormSubmit = this.onInvalidSignatureBlockFormSubmit.bind(
      this
    );
    this.onSignaturePadSubmit = this.onSignaturePadSubmit.bind(this);
    this.isVisible = this.isVisible.bind(this);
    this.updateWindowDimensions = this.updateWindowDimensions.bind(this);
    this.updateSignaturePadRef = this.updateSignaturePadRef.bind(this);
    this.fieldChange = this.fieldChange.bind(this);
    this.textFieldChange = this.textFieldChange.bind(this);
    this.signContract = this.signContract.bind(this);
    this.setDefaultSignatureFormat = this.setDefaultSignatureFormat.bind(this);
  }

  componentWillMount() {
    this.updateWindowDimensions();
  }

  componentDidMount() {
    window.addEventListener('resize', this.updateWindowDimensions);
    window.addEventListener('touchstart', this.setDefaultSignatureFormat);
    window.addEventListener('orientationchange', this.updateWindowDimensions);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateWindowDimensions);
    window.removeEventListener(
      'orientationchange',
      this.updateWindowDimensions
    );
  }

  onSubmit() {
    const { localSignatureBlock } = this.state;
    if (
      localSignatureBlock.name_visibility === 'SHOW' &&
      localSignatureBlock.signature_typed_name &&
      localSignatureBlock.name &&
      localSignatureBlock.signature_typed_name.toLowerCase() !== localSignatureBlock.name.toLowerCase()
    ) {
      GlobalNotifications.show({
        message: 'Unable to sign contract',
        intent: Intent.DANGER
      });
    } else {
      this.signContract();
      this.setState({ submitting: true });
    }
    this.setState({ signatureSubmitted: true });
  }

  onValidSignatureBlockFormSubmit() {
    this.setState({ submitted: true, step: 'sign' });
  }

  onInvalidSignatureBlockFormSubmit() {
    GlobalNotifications.show({
      message: 'Unable to sign contract',
      intent: Intent.DANGER
    });
    this.setState({ submitted: true });
  }

  onSignaturePadSubmit() {
    if (this.state.signaturePadRef.isEmpty()) {
      GlobalNotifications.show({
        message: 'Unable to sign contract',
        intent: Intent.DANGER
      });
    } else {
      this.setState({ submitting: true });
      this.processSignature();
    }
    this.setState({ signatureSubmitted: true });
  }

  setDefaultSignatureFormat() {
    this.setState({
      signatureFormat:
        this.state.window.width < styleVariables.tabletBreakpoint
          ? 'draw'
          : 'type'
    });
    window.removeEventListener('touchstart', this.setDefaultSignatureFormat);
  }

  signatureBlockFields(signatureBlock) {
    return (
      this.isVisible(signatureBlock.name_visibility) ||
      this.isVisible(signatureBlock.email_visibility) ||
      this.isVisible(signatureBlock.phone_visibility) ||
      this.isVisible(signatureBlock.company_name_visibility) ||
      this.isVisible(signatureBlock.title_visibility) ||
      this.isVisible(signatureBlock.address_visibility)
    );
  }

  isVisible(visibility) {
    return visibility === 'SHOW';
  }

  fieldChange(key, value) {
    const state = _.cloneDeep(this.state);
    state.localSignatureBlock[key] = value;
    this.setState(state);
  }

  processSignature() {
    const canvas = this.state.signaturePadRef.getCanvas();
    const rotatedCanvas = this.refs.rotatedCanvas;
    if (window.innerWidth <= window.innerHeight) {
      const ctx = rotatedCanvas.getContext('2d');
      const image = new Image();
      image.src = canvas.toDataURL();
      image.onload = () => {
        rotatedCanvas.width = image.height;
        rotatedCanvas.height = image.width;
        ctx.rotate(90 * Math.PI / 180);
        ctx.translate(0, -canvas.height);
        ctx.drawImage(image, 0, 0);
        this.uploadSignature(rotatedCanvas);
      };
    } else {
      this.uploadSignature(canvas);
    }
  }

  uploadSignature(canvas) {
    canvas.toBlob(blob => {
      client
        .upload(
          blob,
          {},
          { location: 's3', filename: `${this.props.signatureBlock.id}.png` }
        )
        .then(file => {
          const fileProps = {
            signature_url: `https://s3.amazonaws.com/cb-user-uploads/${file.key}`,
            signature_size: file.size,
            signature_mimetype: file.mimetype,
            signature_handle: file.handle
          };
          this.signContract(fileProps);
        })
        .catch(() => {
          GlobalNotifications.show({
            message: 'Unable to sign contract',
            intent: Intent.DANGER
          });
          this.setState({ submitting: false });
          // TODO: Log error to Bugsnag
        });
    }, 'image/png');
  }

  signContract(fileProps) {
    const onError = () => {
      this.setState({ submitting: false });
      GlobalNotifications.show({
        message: 'Unable to sign contract',
        intent: Intent.DANGER
      });
    };
    const onCompleted = () => {
      this.setState({ submitting: false, step: 'success' });
    };

    const mutation = graphql`
      mutation Sign_signContractMutation($input: SignContractInput!) {
        signContract(input: $input) {
          contract {
            ...ContractContainer_node
          }
        }
      }
    `;

    const signatureBlock = _.pick(
      nullifyEmptyStrings(this.state.localSignatureBlock),
      (this.state.localSignatureBlock.signer_fills_fields
        ? ['name', 'email', 'title', 'company_name', 'phone', 'address']
        : []).concat(
        this.state.signatureFormat === 'type' ? 'signature_typed_name' : ''
      )
    );
    signatureBlock.contract_signature_block_id = this.state.localSignatureBlock.id;
    signatureBlock.signature_type =
      this.state.signatureFormat === 'type' ? 'TYPED' : 'PAD';

    const environment = this.props.relay.environment;

    commitMutation(environment, {
      mutation,
      variables: {
        input: {
          ...signatureBlock,
          ...fileProps
        }
      },
      updater: () => {},
      optimisticUpdater: () => {},
      onCompleted,
      onError
    });
  }

  textFieldChange(e) {
    this.fieldChange(e.target.name, e.target.value);
  }

  updateWindowDimensions() {
    setTimeout(() => {
      this.setState({
        window: { width: window.innerWidth, height: window.innerHeight }
      });
    }, 250);
  }

  updateSignaturePadRef(signaturePadRef) {
    this.setState({ signaturePadRef });
  }

  render() {
    const { signatureBlock } = this.props;
    const { localSignatureBlock } = this.state;

    const legalNotice = (
      <p className={styles.legalNotice}>
        I understand that this is a legal representation of my signature.
      </p>
    );

    const SignatureBlockFields = (
      <div>
        <h4>
          Please{' '}
          {localSignatureBlock.signer_fills_fields ? 'provide' : 'confirm'} the
          following details to sign:
        </h4>
        <Formsy.Form
          onValidSubmit={this.onValidSignatureBlockFormSubmit}
          onInvalidSubmit={this.onInvalidSignatureBlockFormSubmit}
          ref='signatureBlockForm'
        >
          {!signatureBlock.signer_fills_fields &&
            <div className={styles.card}>
              <SignatureBlockShow
                signatureBlock={{ node: signatureBlock }}
                style={{ padding: 0 }}
              />
            </div>}
          {signatureBlock.signer_fills_fields &&
            <div>
              {this.isVisible(signatureBlock.name_visibility) &&
                <Input
                  label='Name'
                  name='name'
                  required={this.isVisible(signatureBlock.name_visibility)}
                  validationError='Name is required'
                  value={localSignatureBlock.name || ''}
                  onChange={this.textFieldChange}
                  submitted={this.state.submitted}
                />}
              {this.isVisible(signatureBlock.title_visibility) &&
                <Input
                  label='Title'
                  name='title'
                  required={this.isVisible(signatureBlock.title_visibility)}
                  validationError='Title is required'
                  value={localSignatureBlock.title || ''}
                  onChange={this.textFieldChange}
                  submitted={this.state.submitted}
                />}
              {this.isVisible(signatureBlock.company_name_visibility) &&
                <Input
                  label='Company Name'
                  name='company_name'
                  required={this.isVisible(
                    signatureBlock.company_name_visibility
                  )}
                  validationError='Company name is required'
                  value={localSignatureBlock.company_name || ''}
                  onChange={this.textFieldChange}
                  submitted={this.state.submitted}
                />}
              {this.isVisible(signatureBlock.email_visibility) &&
                <Input
                  label='Email Address'
                  name='email'
                  type='email'
                  required={this.isVisible(signatureBlock.email_visibility)}
                  validationError='Email is required'
                  value={localSignatureBlock.email || ''}
                  onChange={this.textFieldChange}
                  submitted={this.state.submitted}
                />}
              {this.isVisible(signatureBlock.phone_visibility) &&
                <Input
                  label='Phone Number'
                  name='phone'
                  type='tel'
                  required={this.isVisible(signatureBlock.phone_visibility)}
                  validationError='Phone number is required'
                  value={localSignatureBlock.phone || ''}
                  onChange={this.textFieldChange}
                  submitted={this.state.submitted}
                />}
              {this.isVisible(signatureBlock.address_visibility) &&
                <Input
                  label='Address'
                  name='address'
                  autoHeight
                  required={this.isVisible(signatureBlock.address_visibility)}
                  validationError='Address is required'
                  value={localSignatureBlock.address || ''}
                  onChange={this.textFieldChange}
                  submitted={this.state.submitted}
                />}
            </div>}
        </Formsy.Form>
      </div>
    );

    const signatureFormatSwitcher = type =>
      <Button.Group basic>
        <Button
          content='Draw'
          active={this.state.signatureFormat === 'draw'}
          className={type === 'header' ? styles.headerButton : ''}
          onClick={() => this.setState({ signatureFormat: 'draw' })}
        />
        <Button
          content='Type'
          className={type === 'header' ? styles.headerButton : ''}
          active={this.state.signatureFormat === 'type'}
          onClick={() => this.setState({ signatureFormat: 'type' })}
        />
      </Button.Group>;

    const mobileSignatureFormatSwitcher = (
      <div style={{ marginLeft: 'auto', transform: 'translateY(0.5em)' }}>
        {signatureFormatSwitcher('header')}
      </div>
    );

    let showError = false;
    let errorMessage = '';
    const emptySignaturePad =
      this.state.signaturePadRef && this.state.signaturePadRef.isEmpty();
    const signatureNameMismatch =
      localSignatureBlock.name_visibility === 'SHOW' &&
      localSignatureBlock.signature_typed_name &&
      localSignatureBlock.name &&
      localSignatureBlock.signature_typed_name.toLowerCase() !== localSignatureBlock.name.toLowerCase();
    if (this.state.signatureSubmitted) {
      if (this.state.signatureFormat === 'draw') {
        if (emptySignaturePad) {
          showError = true;
        }
      } else {
        if (signatureNameMismatch) {
          showError = true;
          errorMessage = `Signature must match name: ${localSignatureBlock.name}`;
        }
        if (!localSignatureBlock.signature_typed_name) {
          showError = true;
          errorMessage = 'Signature is required';
        }
      }
    }

    const TypeComponent = (
      <div>
        <Formsy.Form
          onSubmit={this.onSubmit}
          ref='signatureForm'
          style={{ position: 'relative' }}
        >
          <input
            name='signature_typed_name'
            required
            autoFocus
            autoComplete='off'
            value={localSignatureBlock.signature_typed_name || ''}
            onChange={this.textFieldChange}
            className={[
              styles.typeSignatureInput,
              showError ? styles.signatureError : '',
              signatureBlockSizeClass(
                localSignatureBlock.signature_typed_name || ''
              )
            ].join(' ')}
          />
          <span
            onClick={() => this.fieldChange('signature_typed_name', '')}
            className={styles.signaturePadReset}
          >
            Clear
          </span>
          <span className={styles.signatureLine}>&times;</span>
          {legalNotice}
        </Formsy.Form>
        {showError &&
          <div className={styles.signatureErrorText}>
            {errorMessage}
          </div>}
      </div>
    );

    const SignButtonContent = (
      <span>
        {/* Wrapped in this condition because loader doesn't hide
          on mobile despite having active set to false */}
        {this.state.submitting &&
          <Loader
            active={this.state.submitting}
            inline
            inverted
            size='mini'
            style={{ marginRight: '1em' }}
          />}
        {this.state.submitting ? 'Submitting Signature' : 'Sign Contract'}
      </span>
    );
    const SuccessMessage = (
      <div style={{ textAlign: 'center' }}>
        <Success />
        <h3 style={{ marginBottom: '2em' }}>You have signed this contract</h3>
        <Button basic fluid content='Done' onClick={this.props.onClose} />
      </div>
    );

    const rotateStyles = {
      height: `${this.state.window.width}px`,
      minHeight: '100%',
      transform: 'rotate(-90deg)',
      width: `${this.state.window.height}px`,
      position: 'absolute'
    };

    return (
      <span>
        {this.state.window.width >= styleVariables.tabletBreakpoint &&
          <Modal
            trigger={this.props.trigger}
            onClose={this.props.onClose}
            onOpen={this.props.onOpen}
            open={this.props.open}
            size='small'
            closeOnDimmerClick={false}
          >
            {this.state.step === 'signatureBlock' && [
              <Modal.Header content='Sign Contract' key='header' />,
              <Modal.Content key='content'>
                <div style={{ maxWidth: '400px', margin: '0 auto' }}>
                  {SignatureBlockFields}
                </div>
              </Modal.Content>,
              <Modal.Actions key='actions'>
                <a onClick={this.props.onClose} className={styles.modalCancel}>
                  Cancel
                </a>
                <Button
                  primary
                  type='submit'
                  onClick={() => this.refs.signatureBlockForm.submit()}
                >
                  Next
                </Button>
              </Modal.Actions>
            ]}
            {this.state.step === 'sign' && [
              <Modal.Header content='Sign Contract' key='header' />,
              <Modal.Content key='content'>
                <div
                  style={{
                    maxWidth: '600px',
                    margin: '0 auto'
                  }}
                >
                  <div style={{ textAlign: 'center' }}>
                    {signatureFormatSwitcher()}
                  </div>
                  <br />
                  {this.state.signatureFormat === 'draw' &&
                    <div>
                      <SignaturePad
                        showError={showError}
                        legalNotice={legalNotice}
                        updateSignaturePadRef={this.updateSignaturePadRef}
                      />
                    </div>}
                  {this.state.signatureFormat === 'type' && TypeComponent}
                </div>
              </Modal.Content>,
              <Modal.Actions key='actions'>
                {this.signatureBlockFields(signatureBlock) &&
                  <a
                    onClick={() => this.setState({ step: 'signatureBlock' })}
                    className={styles.modalCancel}
                  >
                    Back
                  </a>}
                {!this.signatureBlockFields(signatureBlock) &&
                  <a
                    onClick={this.props.onClose}
                    className={styles.modalCancel}
                  >
                    Cancel
                  </a>}
                <Button
                  primary
                  type='submit'
                  disabled={this.state.submitting}
                  onClick={() => {
                    if (this.state.signatureFormat === 'draw') {
                      this.onSignaturePadSubmit();
                    } else {
                      this.refs.signatureForm.submit();
                    }
                  }}
                >
                  {SignButtonContent}
                </Button>
              </Modal.Actions>
            ]}
            {this.state.step === 'success' && [
              <Modal.Header content='Sign Contract' key='header' />,
              <Modal.Content key='content'>
                <div style={{ maxWidth: '280px', margin: '0 auto' }}>
                  {SuccessMessage}
                </div>
              </Modal.Content>
            ]}
          </Modal>}
        {this.state.window.width < styleVariables.tabletBreakpoint &&
          <MobileModal
            trigger={this.props.trigger}
            open={this.props.open}
            onClose={this.props.onClose}
            onOpen={this.props.onOpen}
          >
            {this.state.step === 'signatureBlock' && [
              <MobileModal.Header key='header'>
                <div className='menu'>
                  <a className='item' onClick={this.props.onClose}>
                    Cancel
                  </a>
                </div>
              </MobileModal.Header>,
              <MobileModal.Content key='content'>
                {SignatureBlockFields}
                <br />
                <Button
                  primary
                  type='submit'
                  onClick={() => {
                    this.refs.signatureBlockForm.submit();
                  }}
                >
                  Next
                </Button>
              </MobileModal.Content>
            ]}
            {this.state.step === 'sign' &&
              <div>
                {this.state.signatureFormat === 'draw' &&
                  <div
                    style={
                      this.state.window.width < this.state.window.height
                        ? rotateStyles
                        : {}
                    }
                  >
                    <MobileModal.Header style={{ textAlign: 'center' }}>
                      {this.signatureBlockFields(signatureBlock) &&
                        <div className='menu'>
                          <a
                            className='item'
                            onClick={() =>
                              this.setState({ step: 'signatureBlock' })}
                          >
                            Back
                          </a>
                        </div>}
                      {!this.signatureBlockFields(signatureBlock) &&
                        <div className='menu'>
                          <a className='item' onClick={this.props.onClose}>
                            Cancel
                          </a>
                        </div>}
                      {mobileSignatureFormatSwitcher}
                      <div className='menu right'>
                        {!this.state.submitting &&
                          <a
                            className='item'
                            onClick={this.onSignaturePadSubmit}
                          >
                            Sign
                          </a>}
                        {this.state.submitting &&
                          <a className='item'>
                            <Loader active inline size='tiny' />
                          </a>}
                      </div>
                    </MobileModal.Header>
                    <MobileModal.Content>
                      <canvas
                        ref='rotatedCanvas'
                        width={0}
                        height={0}
                        style={{ display: 'none' }}
                      />
                      <SignaturePad
                        showError={showError}
                        legalNotice={legalNotice}
                        updateSignaturePadRef={this.updateSignaturePadRef}
                      />
                      <br />
                      {this.state.window.height > 350 &&
                        <Button
                          primary
                          type='submit'
                          onClick={this.onSignaturePadSubmit}
                          disabled={this.state.submitting}
                          style={{ zIndex: 1 }}
                        >
                          {SignButtonContent}
                        </Button>}
                    </MobileModal.Content>
                  </div>}
                {this.state.signatureFormat === 'type' &&
                  <div>
                    <MobileModal.Header>
                      {this.signatureBlockFields(signatureBlock) &&
                        <div className='menu'>
                          <a
                            className='item'
                            onClick={() =>
                              this.setState({ step: 'signatureBlock' })}
                          >
                            Back
                          </a>
                        </div>}
                      {!this.signatureBlockFields(signatureBlock) &&
                        <div className='menu'>
                          <a className='item' onClick={this.props.onClose}>
                            Cancel
                          </a>
                        </div>}
                      {mobileSignatureFormatSwitcher}
                      <div className='menu right'>
                        <a
                          className='item'
                          onClick={() => this.refs.signatureForm.submit()}
                        >
                          Sign
                        </a>
                      </div>
                    </MobileModal.Header>
                    <MobileModal.Content>
                      {TypeComponent}
                      <br />
                      <Button
                        primary
                        type='submit'
                        disabled={this.state.submitting}
                        onClick={() => {
                          this.refs.signatureForm.submit();
                        }}
                        fluid
                      >
                        {SignButtonContent}
                      </Button>
                    </MobileModal.Content>
                  </div>}
              </div>}
            {this.state.step === 'success' &&
              <div>
                <MobileModal.Content>
                  {SuccessMessage}
                </MobileModal.Content>
              </div>}
          </MobileModal>}
      </span>
    );
  }
}



// WEBPACK FOOTER //
// ./webpack/components/Document/Contract/Sign.js